import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";

const PersonalDetails = ({ userDetails, setUserDetails, handleChange, handleFileChange, handleDeleteImage, roleLabel }) => {
  const [imageError, setImageError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const imagePath = previewUrl || userDetails.profileImage?.filePath || userDetails.profileImage?.fileUrl;
  const fileInputRef = useRef(null);

  useEffect(() => {
    setImageError(!imagePath);
  }, [imagePath]);

  const onChange = handleChange || ((e) => {
    const { name, value } = e.target || {};
    if (!setUserDetails) return;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  });

  const applyFile = (file) => {
    if (!setUserDetails) return;
    let nextPreview = null;
    if (file) {
      nextPreview = URL.createObjectURL(file);
      setPreviewUrl(nextPreview);
    } else {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setUserDetails((prev) => ({
      ...prev,
      profileFile: file || undefined,
      hasProfileFile: !!file,
      profileImage: file ? { fileUrl: nextPreview } : prev.profileImage,
    }));
  };

  const onFileChange = handleFileChange || ((e) => {
    const file = (e.target && e.target.files && e.target.files[0]) || null;
    applyFile(file);
  });

  // --- Camera capture ---
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;
      setIsCameraOpen(true);
    } catch (err) {
      toast.error("Could not access camera. Check browser permission.");
    }
  };

  // Attach the stream once the dialog's <video> is mounted
  useEffect(() => {
    if (isCameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
    if (!isCameraOpen) stopCamera();
    return stopCamera;
  }, [isCameraOpen]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" });
        applyFile(file);
        setImageError(false);
        setIsCameraOpen(false);
      },
      "image/jpeg",
      0.9
    );
  };

  const onDeleteImage = handleDeleteImage || (() => {
    if (!setUserDetails) return;
    setUserDetails((prev) => ({
      ...prev,
      profileImage: null,
      profileFile: undefined,
      hasProfileFile: false,
    }));
    setImageError(true);
  });

  return (
    <div>
      {/* Avatar Section */}
      <div className="flex flex-column align-items-center mb-4">
        <div className="relative">
          {imageError ? (
            <Avatar
              icon="pi pi-user"
              size="xlarge"
              shape="circle"
              className="bg-primary text-white"
              style={{ width: "120px", height: "120px", fontSize: "3rem" }}
            />
          ) : (
            <img
              src={imagePath}
              alt="Profile"
              onError={() => setImageError(true)}
              className="border-circle shadow-2"
              style={{ width: "120px", height: "120px", objectFit: "cover" }}
            />
          )}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={onFileChange}
          />
          <Button
            icon="pi pi-upload"
            className="p-button-rounded p-button-sm p-button-primary shadow-3"
            style={{ position: "absolute", bottom: "0", right: "0", width: "2rem", height: "2rem" }}
            onClick={() => fileInputRef.current.click()}
            tooltip="Upload photo"
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-camera"
            className="p-button-rounded p-button-sm p-button-help shadow-3"
            style={{ position: "absolute", bottom: "0", left: "0", width: "2rem", height: "2rem" }}
            onClick={openCamera}
            tooltip="Take photo with camera"
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
        {!imageError && (
          <Button
            label="Remove Photo"
            icon="pi pi-trash"
            className="p-button-text p-button-danger p-button-sm mt-2"
            onClick={onDeleteImage}
          />
        )}
        <h3 className="mt-2 mb-0 text-xl font-semibold">{userDetails.name || `${roleLabel || "Your"} Name`}</h3>
        <span className="text-color-secondary text-sm">{userDetails.email || ""}</span>
      </div>

      {/* Camera capture dialog */}
      <Dialog
        header="Take Photo"
        visible={isCameraOpen}
        style={{ width: "480px", maxWidth: "95vw" }}
        onHide={() => setIsCameraOpen(false)}
      >
        <div className="flex flex-column align-items-center gap-3">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", borderRadius: 8, background: "#000" }}
          />
          <div className="flex gap-2">
            <Button label="Capture" icon="pi pi-camera" className="btn-primary" onClick={capturePhoto} />
            <Button label="Cancel" className="p-button-outlined" onClick={() => setIsCameraOpen(false)} />
          </div>
        </div>
      </Dialog>

      <Divider />

      {/* Account Info */}
      <div className="flex align-items-center gap-2 mb-3">
        <i className="pi pi-user text-xl text-primary" />
        <h3 className="text-lg font-semibold m-0">Account Information</h3>
      </div>

      <div className="grid">
        <div className="col-12 md:col-6">
          <label htmlFor="name" className="block text-sm font-medium text-color-secondary mb-1">Full Name</label>
          <InputText
            id="name"
            name="name"
            value={userDetails.name || ""}
            onChange={onChange}
            className="w-full"
            placeholder="Enter your name"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="email" className="block text-sm font-medium text-color-secondary mb-1">Email Address</label>
          <InputText
            id="email"
            name="email"
            value={userDetails.email || ""}
            onChange={onChange}
            className="w-full"
            placeholder="Enter your email"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="phone" className="block text-sm font-medium text-color-secondary mb-1">Phone Number</label>
          <InputText
            id="phone"
            name="phone"
            value={userDetails.phone || ""}
            onChange={onChange}
            className="w-full"
            placeholder="Enter phone number"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="address" className="block text-sm font-medium text-color-secondary mb-1">Address</label>
          <InputTextarea
            id="address"
            name="address"
            value={userDetails.address || ""}
            onChange={onChange}
            rows={1}
            autoResize
            className="w-full"
            placeholder="Enter your address"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
